import React, { useContext, useRef } from 'react';
import PropTypes from 'prop-types';

import PresentationContext from '../PresentationContext';
import XAxis from './XAxis';
import './Dashboard.css';

export const defaultMargins = {
  top: 10,
  right: 80,
  bottom: 20,
  left: 100,
};

export const defaultOffsets = {
  top: 0,
  left: 0,
};

export const zeroMargins = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

let clipPathIdCounter = 0;

function BaseSVG({
  xTicks,
  children,
  className,
  height,
  margins,
  offsets,
  overlay,
  showXAxis,
  svgHeight,
  svgWidth,
  style,
  tickFormat,
  times,
  underlay,
  width,
  xDomainPad,
  yLabel,
  yAxis,
  yAxisTop,
}) {
  const clipPathId = useRef(`canvasClip${++clipPathIdCounter}`);

  const {
    fullHourFormat,
    timeFormat
  } = useContext(PresentationContext);
 
  const hourTickFormat = d => (d.getHours() % 3) ? '' : (timeFormat(fullHourFormat)(d));

  return (
    <>
    <svg
      className={`${className ? `${className} ` : ''}baseSVG`}
      width={svgWidth}
      height={svgHeight}
      viewBox={`${offsets.left} ${offsets.top} ${margins.left + width + margins.right -offsets.left} ${margins.top + height + margins.bottom}`} 
      shapeRendering="crispEdges"
      style={{
        width: svgWidth,
        paddingBottom: `${100*(margins.top + height + margins.bottom)/(margins.left + width + margins.right)}%`,
        height: svgHeight,
        overflow: "visible",
        ...style,
      }}
      preserveAspectRatio="xMidYMin slice"
    > 
      <defs>
        <clipPath id={clipPathId.current} className="canvasClip">
          <rect x="0.5" width={width} height={height} />
        </clipPath>
        <clipPath id={`hC${clipPathId.current.substr(1)}`} className="hCanvasClip">
          <rect width={width} height={margins.top + height + margins.bottom} />
        </clipPath>
      </defs>
      <g transform={`translate(${margins.left}, ${margins.top})`}>
        <g className="pixel-adjust" transform="translate(0.5,0.5)">
          <line vectorEffect="non-scaling-stroke" className="time" y2={height + xDomainPad} />
          <line vectorEffect="non-scaling-stroke" className="time" x1={width} x2={width} y2={height + xDomainPad}/>
        </g>
        {
          underlay
        }
        {showXAxis ? <XAxis xTicks={xTicks} y={height + 0.5} width={width} times={times} tickFormat={tickFormat || hourTickFormat} /> : undefined}
        {yAxisTop ? undefined : yAxis}
        <g className="canvas" clipPath={`url(#${clipPathId.current})`} shapeRendering="auto">
          <rect className="canvasBackground" width={width} height={height} />
          <line className="hairline cursor" y1={0} y2={height} />
          {
            children
          }
        </g>
        {yLabel ? (
          <g className="labels" transform={`translate(${width}, 0)`}>
            {yLabel}
          </g>
        ) : undefined}
        {yAxisTop ? yAxis : undefined}
        {
          overlay
        }
      </g>
    </svg> 
    </>
  );
}

BaseSVG.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  height: PropTypes.number,
  tickFormat: PropTypes.func,
  margins: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
  offsets: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
  }),
  overlay: PropTypes.node,
  showXAxis: PropTypes.bool,
  svgHeight: PropTypes.oneOfType([PropTypes.number,PropTypes.string]),
  svgWidth: PropTypes.oneOfType([PropTypes.number,PropTypes.string]),
  times: XAxis.propTypes.times,
  underlay: PropTypes.node,
  width: PropTypes.number,
  xDomainPad: PropTypes.number,
  yLabel: PropTypes.node,
  yAxis: PropTypes.element,
  yAxisTop: PropTypes.bool,
};

BaseSVG.defaultProps = {
  children: undefined,
  className: undefined,
  height: 350,
  tickFormat: undefined,
  margins: defaultMargins,
  offsets: defaultOffsets,
  overlay: undefined,
  underlay: undefined,
  showXAxis: true,
  svgHeight: "1px",
  svgWidth: "100%",
  times: undefined,
  width: 720,
  xDomainPad: 7,
  yLabel: undefined,
  yAxis: undefined,
  yAxisTop: false,
};

export default BaseSVG;
